/**
 * keyboard.js
 * Keyboard shortcuts and accessibility functions
 */

import { elements } from './dom.js?v=302';
import { state } from './state.js?v=302';
import { createNewChat } from './chat.js?v=302';
import { toggleSidebar } from './ui.js?v=302';

/**
 * Handle keyboard shortcuts for chat operations
 * @param {KeyboardEvent} e - Keyboard event
 */
export function handleKeyboardShortcuts(e) {
  // Escape key to cancel generation
  if (e.key === "Escape" && state.isGenerating) {
    e.preventDefault();
    if (state.controller) {
      state.controller.abort();
    }
    return;
  }

  if (e.ctrlKey || e.metaKey) {
    switch (e.key) {
      case "k":
        e.preventDefault();
        createNewChat();
        break;
      case "e":
        e.preventDefault();
        // Export chats - dynamically import to avoid circular deps
        import('./storage.js?v=302').then(({ exportChats }) => {
          exportChats();
        });
        break;
      case ",":
        e.preventDefault();
        elements.settingsModal.classList.add("visible");
        break;
      case "/":
        e.preventDefault();
        elements.shortcutsModal.classList.add("visible");
        break;
    }
  }
}

/**
 * Handle global keyboard shortcuts
 * @param {KeyboardEvent} e - Keyboard event
 */
export function handleGlobalKeyboardShortcuts(e) {
  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  const modifier = isMac ? e.metaKey : e.ctrlKey;

  if (modifier && e.key === 'n') {
    e.preventDefault();
    createNewChat();
  }

  if (modifier && e.key === 'd') {
    e.preventDefault();
    // Toggle theme - dynamically import to avoid circular deps
    import('./themes.js?v=302').then(({ toggleTheme }) => {
      toggleTheme();
    });
  }

  if (modifier && e.key === 'b') {
    e.preventDefault();
    toggleSidebar();
  }

  if (e.key === '?' && !e.target.matches('input, textarea')) {
    e.preventDefault();
    const modal = document.getElementById('keyboard-shortcuts-modal');
    if (modal) {
      modal.classList.add('visible');
      trapFocus(modal);
    }
  }

  if (e.key === 'Escape') {
    const modals = document.querySelectorAll('.modal.visible');
    modals.forEach(modal => modal.classList.remove('visible'));
  }
}

/**
 * Focus trap for modals - prevents focus from escaping the modal
 * @param {HTMLElement} element - Modal element to trap focus within
 */
export function trapFocus(element) {
  const focusableElements = element.querySelectorAll(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  );
  const firstFocusable = focusableElements[0];
  const lastFocusable = focusableElements[focusableElements.length - 1];

  element.addEventListener('keydown', (e) => {
    if (e.key === 'Tab') {
      if (e.shiftKey && document.activeElement === firstFocusable) {
        e.preventDefault();
        lastFocusable.focus();
      } else if (!e.shiftKey && document.activeElement === lastFocusable) {
        e.preventDefault();
        firstFocusable.focus();
      }
    }
  });
}
